import { ChannelLayoutType, LAYOUT_COLUMN_GAP } from "../../Channel/ChannelDataTypes";
import { CellDataTypes, ClusterApi } from '@cluster/web-components'
import { AppThunk, ApiError } from "../misc";
import { doLogoutUser } from "./user";
import { doGetLayoutCells } from "./cells";
import { ROUTE_ACTIONS } from "../routes";
import { ComputedClusterLayout } from "@cluster/web-components/dist/ClusterApi/ClusterApi";
import firebase from '../../lib/firebase';
import { doRouteSync } from "./location";

export enum LAYOUT_ACTIONS {
  FETCH_LAYOUT_START = "FETCH_LAYOUT_START",
  FETCH_LAYOUT_SUCCESS = "FETCH_LAYOUT_SUCCESS",
  FETCH_LAYOUT_ERROR = "FETCH_LAYOUT_ERROR",
  LAYOUT_UPDATE_BECAME_AVAILABLE = "LAYOUT_UPDATE_BECAME_AVAILABLE",
  LAYOUT_UPDATE_BECAME_UNAVAILABLE = "LAYOUT_UPDATE_BECAME_UNAVAILABLE",
  CELL_FOCUSED = "CELL_FOCUSED",
  CELL_UNFOCUSED = "CELL_UNFOCUSED",
  CHANNEL_POPUP_CLOSED = "CHANNEL_POPUP_CLOSED",
  PAN_TO_CELL = "PAN_TO_CELL",
  STOPPED_PANNING_TO_CELL = "STOPPED_PANNING_TO_CELL",
  LAYOUT_LOADED_SET = "LAYOUT_LOADED_SET"
}

export type LAYOUT_ACTION_TYPES = FetchLayoutAction | LayoutUpdateAvailabilityAction | CellFocusAction | ChannelPopupAction | CellPanAction | LayoutLoadedAction

export type FetchLayoutAction = {
  type: LAYOUT_ACTIONS.FETCH_LAYOUT_START | LAYOUT_ACTIONS.FETCH_LAYOUT_SUCCESS | LAYOUT_ACTIONS.FETCH_LAYOUT_ERROR,
  payload?: ChannelLayoutType,
  error?: ApiError
}

export type LayoutUpdateAvailabilityAction = {
  type: LAYOUT_ACTIONS.LAYOUT_UPDATE_BECAME_AVAILABLE | LAYOUT_ACTIONS.LAYOUT_UPDATE_BECAME_UNAVAILABLE
}

export type CellFocusAction = {
  type: LAYOUT_ACTIONS.CELL_FOCUSED | LAYOUT_ACTIONS.CELL_UNFOCUSED
  payload?: number
}

export type ChannelPopupAction = {
  type: LAYOUT_ACTIONS.CHANNEL_POPUP_CLOSED
}

export type CellPanAction = {
  type: LAYOUT_ACTIONS.PAN_TO_CELL,
  meta: undefined,
  cellId: number
} | {
  type: LAYOUT_ACTIONS.STOPPED_PANNING_TO_CELL,
  meta: undefined,
  cellId: undefined
}

export type LayoutLoadedAction = {
  type: LAYOUT_ACTIONS.LAYOUT_LOADED_SET,
  payload: boolean
}

const calculateChannelDimensions = (layout: ComputedClusterLayout) => {
  // Calculate channel width and height.
  let channelWidth: number = 0;
  let channelHeight: number = 0;

  if (layout.layoutConfig?.type === "columns" && layout.layoutConfig.columnLayouts) {
    for(const columnConfig of layout.layoutConfig.columnLayouts) {
      channelWidth += columnConfig.width * CellDataTypes.CELL_WIDTH + (columnConfig.width-1) * CellDataTypes.CELL_GAP;
    }

    channelHeight = layout.layoutConfig.height! * CellDataTypes.CELL_HEIGHT + (layout.layoutConfig.height!-1) * CellDataTypes.CELL_GAP;
    // Add column gap to width calc
    channelWidth += (layout.layoutConfig.columnLayouts.length - 1) * (LAYOUT_COLUMN_GAP + CellDataTypes.CELL_GAP*2);
  } else {
    channelWidth = layout.size! * CellDataTypes.CELL_WIDTH + (layout.size!-1) * CellDataTypes.CELL_GAP;
    channelHeight = layout.size! * CellDataTypes.CELL_HEIGHT + (layout.size!-1) * CellDataTypes.CELL_GAP;
  }

  return { channelWidth, channelHeight };
}

const translateLayoutResponse = (layout: ComputedClusterLayout) => {
  let columns: ClusterApi.ComputedClusterCell[][] = [];

  if (layout.layoutConfig?.type === "columns" && layout.layoutConfig.columnLayouts && (layout.layoutConfig.columnLayouts?.length??0) > 0) {
    for (const cell of layout.layout) {
      const row = Math.ceil(cell.position/layout.size);
      const gridCol = ((cell.position-1) % layout.size) + 1;

      let lastCol = 0;
      for (let i=0; i<layout.layoutConfig.columnLayouts.length; i++) {
        if (gridCol > lastCol && gridCol <= layout.layoutConfig.columnLayouts[i].width+lastCol) {
          if (typeof columns[i] === "undefined") {
            columns[i] = [];
          }
          columns[i].push({
            ...cell,
            position: (row-1)*layout.layoutConfig.columnLayouts[i].width + gridCol - lastCol
          })
          break;
        }
        lastCol += layout.layoutConfig.columnLayouts[i].width;
      }
    }
  }

  const {channelWidth, channelHeight} = calculateChannelDimensions(layout);

  return {
    size: layout.size,
    layoutConfig: layout.layoutConfig,
    layout: layout.layoutConfig?.type !== "columns" ? layout.layout : columns,
    boardId: layout.boardId,
    centroidCellId: layout.centroidCellId,
    static: layout.static,
    channelWidth,
    channelHeight,
  }
}

export const doGetLayout = (channelId: number, centroidId?: number, familyTag?: string, favoritesOnly?: boolean, todaysUpdatesOnly?: boolean): AppThunk => async (dispatch, getState, api) => {
  dispatch({type: LAYOUT_ACTIONS.FETCH_LAYOUT_START});

  const cellFocused = getState().channelLayout.focusedCell !== false;

  // Hide a focused cell
  if (cellFocused) {
    dispatch(doUnfocusCell());
  }

  try {
    let data = await api.getBoardLayout(channelId, { centroidId, familyTag, favoritesOnly, todaysUpdatesOnly, clientOptions: ['supportsColumns'] });

    let payload: ChannelLayoutType = {
      ...translateLayoutResponse(data),
      activeTag: familyTag??""
    }

    dispatch({
      type: LAYOUT_ACTIONS.FETCH_LAYOUT_SUCCESS,
      payload
    });

  } catch (message) {
    try {
      const error = JSON.parse(message.message);

      dispatch({
        type: LAYOUT_ACTIONS.FETCH_LAYOUT_ERROR,
        error
      });

      if (error.errorCode === "AUTH_INVALID_TOKEN" || error.errorCode === "AUTH_HEADER_MISSING" || error.errorCode === "AUTH_EXPIRED_TOKEN" || error.errorCode === "AUTH_PASSWORD_CHANGED") {
        dispatch(doLogoutUser("Your session has expired."));
      }
    } catch(badError) {
      dispatch({
        type: LAYOUT_ACTIONS.FETCH_LAYOUT_ERROR,
        error: {
          developerMessage: message.message,
          userMessage: "An unknown error occured"
        }
      });
    }
  }
}

export const doImportLayout = (raw_json: string): AppThunk => async (dispatch, getState, api) => {

  const cellFocused = getState().channelLayout.focusedCell !== false;

  // Hide a focused cell
  if (cellFocused) {
    dispatch(doUnfocusCell());
  }

  try {
    let data = JSON.parse(raw_json);

    let payload: ChannelLayoutType = {
      ...translateLayoutResponse(data),
      activeTag: ""
    }

    dispatch({
      type: LAYOUT_ACTIONS.FETCH_LAYOUT_SUCCESS,
      payload
    });

    dispatch(doGetLayoutCells());
  } catch (message) {
    alert(message);
  }
}

// Updates current layout
export const doUpdateLayout = (): AppThunk => async (dispatch, getState) => {
  const state = getState();

  const { boardId, centroidCellId, activeTag } = state.channelLayout.data;

  return dispatch(doGetLayout(boardId, centroidCellId, activeTag));
}

// Updates current layout and gets all cells
export const doUpdateLayoutAndCells = (): AppThunk => async (dispatch, getState) => {
  const state = getState();

  await dispatch(doUpdateLayout());

  // Firebase event
  firebase.analytics.logEvent("channel_refresh", { channel_id: state.channelLayout.data.boardId });

  return dispatch(doGetLayoutCells());
}

// Updates current layout and gets the new cells it may have
// UNUSED
// @TODO remove? use case was to download only newly added cells from the updated layout.
// the issue was pulling old cells from cache that have not been updated.
// export const doUpdateLayoutWithNewCells = (): AppThunk => async (dispatch, getState) => {
//   const state = getState();

//   const currentCellIds = state.channelLayout.data.layout.map(cellLayout => cellLayout.cell);
//   await dispatch(doUpdateLayout());
//   const updatedCellIds = state.channelLayout.data.layout.map(cellLayout => cellLayout.cell);

//   const newCellIds = currentCellIds.filter(cid => !updatedCellIds.includes(cid));

//   await dispatch(doGetCells(newCellIds));
// }

export const doSetLayoutUpdateAvailability = (available: boolean) => {
  return {
    type: available ? LAYOUT_ACTIONS.LAYOUT_UPDATE_BECAME_AVAILABLE : LAYOUT_ACTIONS.LAYOUT_UPDATE_BECAME_UNAVAILABLE
  }
}

export const doFocusCell = (cellId: number): CellFocusAction => {
  return {
    type: LAYOUT_ACTIONS.CELL_FOCUSED,
    payload: cellId
  }
}

export const doUnfocusCell = (): CellFocusAction => {
  return {
    type: LAYOUT_ACTIONS.CELL_UNFOCUSED,
  }
}

export const doToggleFocusCell = (cellId: number): AppThunk => async (dispatch, getState) => {
  const currentFocus = getState().channelLayout.focusedCell;

  if (cellId !== currentFocus) {
    dispatch(doFocusCell(cellId));
  } else {
    dispatch(doUnfocusCell());
  }
}

export const doCloseChannelPopup = (): ChannelPopupAction => {
  return {
    type: LAYOUT_ACTIONS.CHANNEL_POPUP_CLOSED,
  }
}

export const doCloseChannelBlur = (): AppThunk => async (dispatch, getState) => {
  const state = getState();

  if (state.channelLayout.focusedCell !== false) {
    dispatch(doUnfocusCell());
  } else if (state.channelLayout.channelPopup !== "none") {
    dispatch(doCloseChannelPopup());
  }
}

export const doPanToCell = (cellId: number): CellPanAction => {
  return {
    type: LAYOUT_ACTIONS.PAN_TO_CELL,
    cellId,
    meta: undefined
  }
}

export const doStopPanningToCell = (): AppThunk => (dispatch, getState) => {
  // Stop pan in state
  const stopPanAction: CellPanAction = {
    type: LAYOUT_ACTIONS.STOPPED_PANNING_TO_CELL,
    cellId: undefined,
    meta: undefined
  }

  dispatch(stopPanAction);

  // Remove pan from query if it exists
  const state = getState();
  let { type, payload, query } = state.location;

  if (typeof query?.pan !== "undefined") {
    delete query.pan;
    return dispatch(doRouteSync(state, type as ROUTE_ACTIONS, {payload, query}, true));
  }
}

export const doSetLayoutLoaded = (layoutLoaded: boolean) => {
  return {
    type: LAYOUT_ACTIONS.LAYOUT_LOADED_SET,
    payload: layoutLoaded
  }
}
